import React, { forwardRef, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { AppStorage } from "../Utils/Context/AppContext";
import Request from "../Utils/Request/Request";
import { showToast } from "../Utils";
import moment from "moment";
import { getDocumentId } from "../Utils/documentNumber";

const money = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : "0.00";
};

const Detail = ({ label, arabic, value }) => (
  <div className="premium-invoice__detail">
    <span className="premium-invoice__detail-label">{label}<small>{arabic}</small></span>
    <strong>{value || "—"}</strong>
  </div>
);

const Normal1InvoiceHtml = forwardRef(({ flag, sales }, ref) => {
  const { id } = useParams();
  const AppContext = useContext(AppStorage);
  const [invoiceData, setInvoiceData] = useState(null);
  const [userData, setUserData] = useState(null);

  const getPrintInvoice = useCallback(() => {
    Request.get(`/getPrintInvoice/${id}`)
      .then((result) => {
        setInvoiceData(result.data.invoice);
        setUserData(result.data.UserData);
      })
      .catch((err) => {
        showToast("error", err.response?.status === 401 ? "Unauthorized user." : "Something went wrong.");
      });
  }, [id]);

  useEffect(() => {
    if (AppContext?.userAuthToken) getPrintInvoice();
  }, [AppContext?.userAuthToken, getPrintInvoice]);

  useEffect(() => {
    if (!invoiceData) return;
    const number = getDocumentId(invoiceData);
    const prefix = sales ? "return" : flag === true ? "offer" : "invoice";
    document.title = `${prefix}_${number || ""}_${invoiceData.companyName || invoiceData.supplierName || ""}`;
  }, [flag, invoiceData, sales]);

  const vatAmount = useMemo(() => {
    if (invoiceData?.vat != null) return Number(invoiceData.vat) || 0;
    return (invoiceData?.invoiceproducts || []).reduce((sum, item) => sum + Number(item?.vat || 0), 0);
  }, [invoiceData]);

  const netAmount = Number(invoiceData?.totalAfterVat);
  const finalAmount = Number.isFinite(netAmount)
    ? netAmount
    : Number(invoiceData?.total || 0) + vatAmount - Number(invoiceData?.discount || 0);

  const isSimplifiedInvoice = ["simplified", "simsi"].includes(
    String(invoiceData?.invoice_category || invoiceData?.invoiceCategory || "").toLowerCase()
  );

  const documentTitle = sales
    ? invoiceData?.zatca_return_type === "DEBIT_NOTE"
      ? ["DEBIT NOTE", "إشعار مدين"]
      : ["CREDIT NOTE", "إشعار دائن"]
    : flag === true
      ? ["QUOTATION", "عرض سعر"]
      : flag === "purchases"
        ? ["PURCHASE INVOICE", "فاتورة مشتريات"]
        : isSimplifiedInvoice
          ? ["SIMPLE INVOICE", "فاتورة مبسطة"]
          : ["TAX INVOICE", "فاتورة ضريبية"];

  const customerName = flag === "purchases" ? invoiceData?.supplierName : invoiceData?.companyName;
  const showDiscount = AppContext?.userInfo?.addDiscount === true;
  const products = invoiceData?.invoiceproducts || [];

  return (
    <div className="premium-invoice" ref={ref}>
      <header className="premium-invoice__header">
        <div className="premium-invoice__brand">
          {userData?.logo ? (
            <img
              src={`${process.env.REACT_APP_API_URL || "http://localhost:3001"}/image-storage/${userData.logo}`}
              alt={userData?.companyName || "Company logo"}
            />
          ) : (
            <div className="premium-invoice__monogram">{(userData?.companyName || "D").charAt(0)}</div>
          )}
          <div>
            <h1>{userData?.companyName || "Company Name"}</h1>
            <p dir="rtl">{userData?.companyNameAR}</p>
          </div>
        </div>
        <div className="premium-invoice__seller">
          <Detail label="Commercial Registration" arabic="السجل التجاري" value={userData?.companyNumber} />
          <Detail label="VAT Number" arabic="الرقم الضريبي" value={userData?.vatNumber} />
          <Detail label="Address" arabic="العنوان" value={userData?.address} />
        </div>
      </header>

      <section className="premium-invoice__title-row">
        <span />
        <div className="premium-invoice__title">
          <strong>{documentTitle[0]}</strong>
          <b dir="rtl">{documentTitle[1]}</b>
        </div>
        <span />
      </section>

      <section className="premium-invoice__summary">
        <div className="premium-invoice__meta-card">
          {(sales || invoiceData?.salesReturn) && <Detail label="Document Number" arabic="رقم المستند" value={getDocumentId(invoiceData)} />}
          {invoiceData?.po && <Detail label="Reference / P.O." arabic="المرجع" value={invoiceData.po} />}
          <Detail label="Invoice No." arabic="رقم الفاتورة" value={getDocumentId(invoiceData)} />
          <Detail
            label="Invoice Date"
            arabic="تاريخ الفاتورة"
            value={invoiceData?.invoiceDate ? moment(invoiceData.invoiceDate).format("DD-MM-YYYY") : null}
          />
        </div>

        <div className="premium-invoice__customer-card">
          <div className="premium-invoice__section-label">BILL TO <span dir="rtl">فاتورة إلى</span></div>
          <Detail label="Customer" arabic="اسم العميل" value={customerName} />
          <Detail label="VAT Number" arabic="الرقم الضريبي" value={invoiceData?.vatNumber} />
          <Detail label="Address" arabic="العنوان" value={invoiceData?.address} />
          <Detail label="Payment Terms" arabic="طريقة الدفع" value={invoiceData?.payment} />
        </div>

      </section>

      {(invoiceData?.notes || invoiceData?.notes_2) && (
        <section className="premium-invoice__notes">
          <strong>Notes <span dir="rtl">ملاحظات</span></strong>
          <p>{[invoiceData.notes, invoiceData.notes_2].filter(Boolean).join(" · ")}</p>
        </section>
      )}

      <section className="premium-invoice__items">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Item <small>الصنف</small></th>
              <th>Description <small>الوصف</small></th>
              <th>Qty <small>الكمية</small></th>
              <th>Unit Price <small>سعر الوحدة</small></th>
              {showDiscount && <th>Discount <small>الخصم</small></th>}
              <th>VAT <small>الضريبة</small></th>
              <th>Total <small>الإجمالي</small></th>
            </tr>
          </thead>
          <tbody>
            {products.map((item, index) => (
              <tr key={item.id || index}>
                <td>{index + 1}</td>
                <td className="premium-invoice__item-name">{item?.productName || "—"}<small>{item?.unit}</small></td>
                <td>{item?.description || "—"}</td>
                <td>{item?.qty}</td>
                <td>{money(item?.unitPrice)}</td>
                {showDiscount && <td>{money(item?.discount)}</td>}
                <td>{money(item?.vat)}</td>
                <td>{money(item?.totalAfterVat ?? (Number(item?.total || 0) + Number(item?.vat || 0)))}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="premium-invoice__closing">
        {invoiceData?.bank_iban && (
          <div className="premium-invoice__bank">
            <div className="premium-invoice__section-label">BANK DETAILS <span dir="rtl">معلومات الحساب البنكي</span></div>
            <p>IBAN</p>
            <strong>{invoiceData.bank_iban}</strong>
          </div>
        )}

        <div className="premium-invoice__settlement">
          {flag === false && invoiceData?.qr && (
            <div className="premium-invoice__qr-card premium-invoice__qr-card--large">
              <img src={invoiceData.qr} alt="ZATCA QR code" />
              <span>Scan to verify</span>
              <small dir="rtl">امسح للتحقق</small>
            </div>
          )}
          <div className="premium-invoice__totals">
            <div><span>Total before VAT <small>الإجمالي قبل الضريبة</small></span><strong>{money(invoiceData?.total)}</strong></div>
            {showDiscount && <div><span>Discount <small>الخصم</small></span><strong>{money(invoiceData?.discount)}</strong></div>}
            {(invoiceData?.extravalues || []).map((item, index) => (
              <div key={`extra-${index}`}><span>{item.text}</span><strong>{money(item.value)}</strong></div>
            ))}
            {(invoiceData?.addedvalues || []).map((item, index) => (
              <div key={`added-${index}`}><span>{item.text}</span><strong>{money(item.value)}</strong></div>
            ))}
            <div><span>VAT {AppContext?.userInfo?.vat || 0}% <small>ضريبة القيمة المضافة</small></span><strong>{money(vatAmount)}</strong></div>
            <div className="premium-invoice__grand-total">
              <span>AMOUNT DUE <small>المبلغ الإجمالي شامل الضريبة</small></span>
              <strong>{money(finalAmount)} <em>SAR</em></strong>
            </div>
          </div>
        </div>
      </section>

      <footer className="premium-invoice__footer">
        <div><strong>Thank you for your business</strong><span dir="rtl">شكرًا لتعاملكم معنا</span></div>
        <p>{userData?.address}</p>
      </footer>
    </div>
  );
});

export default Normal1InvoiceHtml;
